/* eslint-disable react-hooks/exhaustive-deps */
import DirectorNotesButton from "components/DirectorNotesButton";
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import AssignedProsecutorToCourt from "pages/Court/fragments/AssignProsecutorToCourt";
import DailyReportFeedbackSection from "pages/Court/fragments/DailyReportFeedbackSection";
import ReAssignedProsecutorToCourt from "pages/Court/fragments/ReAssignProsecutorToCourt";
import Report from "pages/Court/fragments/Report";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addChargeDraftHistory,
  editCaseCreation,
  editCaseCreationWithupload,
  fetchCaseCreationById,
  fetchCaseCreationRelatedParties,
} from "services/caseServices";
import {
  CaseCreation,
  DailyReport,
  DocumentSchema,
  Exhibit,
  FEEDBACK_MODEL,
  FILE_URL,
  Reporter,
  Roles,
  Suspect,
  Victim,
  Witness,
} from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";
import FeedbackSection from "./FeedbackSection";
import ChangeLog from "./modal/ChangeLog";
import CountList from "./modal/CountList";
import CourtList from "./modal/CourtList";
// import DefendantList from "./modal/ClosedSusectsList";
import DocumentViewerModal from "components/DocumentViewer";
import CourtReport from "pages/Court/fragments/CourtReport";
import ClosedSusectsList from "./modal/ClosedSusectsList";
import DefendantList from "./modal/DefendantList";
import ExibitList from "./modal/ExibitList";
import RespondBackComment from "./modal/RespondBackComment";
import WitnessList from "./modal/WitnessList";
import ChargeDraft from "./modal/ChargeDraftList";
import CourtApplicationTab from "./modal/CourtApplicationTab";
import RemandTab from "./modal/RemandTab";
import ExecutionTab from "./modal/ExecutionTab";
import { useTranslation } from "react-i18next";

type Props = {
  caseId: string | number;
};

const detailTabs = [
  { key: "defendant" },
  { key: "witness" },
  { key: "exibit" },
  { key: "count" },
  { key: "chargeDraft" },
];

const extraTabs = [
  { key: "court" },
  { key: "hearing" },
  { key: "report" },
  { key: "courtApplication" },
  { key: "remand" },
  { key: "execution" },
];

// A single, consistently-styled action row in the "Take Action" menu.
const ActionItem: React.FC<{
  icon: string;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-800 hover:bg-red-50 transition-colors"
  >
    <span className="shrink-0 text-base leading-none">{icon}</span>
    <span className="flex-1">{label}</span>
    <svg
      className="w-4 h-4 shrink-0 text-gray-300"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  </button>
);

const InstituteOfChargeDetail: React.FC<Props> = ({ caseId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [caseRelatedParties, setCaseRelatedParties] = useState<{
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
    exhibits: Exhibit[];
  }>();

  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  // Clerk gets the registrar's "add court detail" capability (in addition).
  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const canAddFeedback = hasRole(loginData?.roles, [
    Roles.generalAttorney,
    Roles.director,
    Roles.coordinator,
    Roles.prosecutor,
  ]);

  const [caseData, setCaseData] = useState<CaseCreation>();
  // The prosecutor assigned (or re-assigned) to this case acts as its
  // prosecutor (edit, re-upload and forward the charge draft), the same rule
  // the charge list uses, whatever their role name.
  const isProsecutor =
    hasRole(loginData?.roles, [Roles.prosecutor]) ||
    (!!loginData?.user_id &&
      [caseData?.assigned_prosecutor_userid, caseData?.reassign_prosecutor_userid]
        .filter(Boolean)
        .some((id) => String(id) === String(loginData.user_id)));
  const [selectedDailyReport, setSelectedDailyReport] = useState<DailyReport>();
  const [activePage, setActivePage] = useState("defendant");
  const [activeSidebar, setActiveSidebar] = useState("feedbackSection");
  const [loading, setLoading] = useState<boolean>(true);
  const [uploadChargeDraft, setUploadChargeDraft] = useState<File | null>();
  const [finalDecision, setFinalDecision] = useState<File | null>();
  const [finalDecisionSubmitting, setFinalDecisionSubmitting] = useState(false);
  const [isReassignProsecutor, setIsReassignProsecutor] = useState(false);
  const [finalDecisionDocument, setFinalDecisionDocument] = useState<
    DocumentSchema | undefined
  >(undefined);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [openRespondBack, setOpenRespondBack] = useState(false);
  const actionDropdownRef = React.useRef<HTMLDivElement>(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      fetchCaseCreationById(caseId || ""),
      fetchCaseCreationRelatedParties(caseId || ""),
    ])
      .then(([caseRes, partiesRes]) => {
        setCaseData(caseRes as CaseCreation);
        setCaseRelatedParties(partiesRes as any);
        setLoading(false);
      })
      .catch((err) => {
        showTooltip(
          errorHandlerOnForm(err) || t("failed_to_load_case"),
          "error",
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    if (caseId) fetchAll();
  }, [caseId]);

  useEffect(() => {
    if (caseData?.status === "Respond_Back_prosecutor") {
      setActiveSidebar("respondBack");
    } else if (caseData?.is_article_of_cahrge_changed) {
      setActiveSidebar("artileOfChange");
    }
  }, [caseData]);

  useEffect(() => {
    if (caseData?.documents) {
      // Only flag a real final-decision document; don't override the default
      // view (Feedback) just because the case has any documents.
      const finalDoc = caseData.documents.find(
        (doc) => doc.document_type === "final_court_decision",
      );
      setFinalDecisionDocument(finalDoc);
    }
  }, [caseData?.documents]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(event.target as Node)
      ) {
        setActionDropdownOpen(false);
      }
    }
    if (actionDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actionDropdownOpen]);

  // Final decision upload — used by the prosecutor's form sidebar.
  const handleUploadFinalDecision = () => {
    setActionDropdownOpen(false);
    if (finalDecisionSubmitting) return; // guard against double-submit
    if (!finalDecision) {
      setActiveSidebar("form");
      showTooltip(t("please_upload_final_decision_file"), "error");
      return;
    }
    setFinalDecisionSubmitting(true);
    editCaseCreationWithupload({
      id: caseData?.id,
      final_court_decision_files: finalDecision,
      // The final verdict completes the court-hearing stage.
      status_filter: "final_verdict_given",
    })
      .then(() => {
        showTooltip(t("successful"), "success");
        setFinalDecision(null);
        setActiveSidebar("finalDecision");
        fetchAll();
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_forwarding_case"),
          "error",
        );
      })
      .finally(() => setFinalDecisionSubmitting(false));
  };

  const renderActionDropdown = () => {
    if (!caseData) return null;

    const files = caseData?.opening_case_files;

    const canRespondBack =
      caseData?.status === "forwarded_to_coordinator" && isCoordinator;

    const canForward =
      ((caseData?.status === "Pending" ||
        caseData?.status === "Respond_Back_prosecutor") &&
        isProsecutor) ||
      (caseData?.status === "forwarded_to_coordinator" && isCoordinator) ||
      (caseData?.status === "forwarded_to_registrar" &&
        (isRegistrar || isClerk) &&
        !!files &&
        files.length === 0);

    const canAddVenueCourtDetail =
      (isRegistrar || isClerk) &&
      !!caseData?.change_of_venue?.find((v) => v?.status === "approved");

    const canUpdateCourtDetail =
      isRegistrar &&
      !!caseData?.is_case_updated_by_court &&
      caseData?.status === "forwarded_to_registrar";

    const canAssignProsecutor =
      !!files &&
      files[0]?.status === "forwarded_to_coordinator" &&
      isCoordinator &&
      !!caseData?.case_investigation_links;

    const canReassignProsecutor =
      isCoordinator && !!files && !!files[0]?.assign_prosecutor;

    const canUploadFinalDecision =
      isProsecutor &&
      !caseData?.is_case_updated_by_court &&
      !!files &&
      files.length > 0;

    const forwardLabel =
      caseData?.status === "Pending" ||
      caseData?.status === "Respond_Back_prosecutor"
        ? t("forward_to_coordinator")
        : caseData?.status === "forwarded_to_coordinator"
          ? t("forward_to_registrar")
          : t("add_court_detail");

    const handleForward = () => {
      if (caseData?.status === "Pending" && !uploadChargeDraft) {
        setActivePage("chargeDraft");
        showTooltip(t("please_upload_charge_draft_file"), "error");
        return;
      }
      if (caseData?.status === "forwarded_to_registrar") {
        navigate("/court-detail", { state: { caseData: caseData } });
        return;
      }

      const api =
        caseData?.status === "Pending"
          ? editCaseCreationWithupload
          : caseData?.status === "Respond_Back_prosecutor" && uploadChargeDraft
            ? editCaseCreationWithupload
            : editCaseCreation;

      api({
        id: caseData?.id,
        status:
          caseData?.status === "Pending" ||
          caseData?.status === "Respond_Back_prosecutor"
            ? "forwarded_to_coordinator"
            : caseData?.status === "forwarded_to_coordinator"
              ? "forwarded_to_registrar"
              : "sent_to_court",
        charge_draft_files:
          caseData?.status === "Pending" ||
          (caseData?.status === "Respond_Back_prosecutor" && uploadChargeDraft)
            ? uploadChargeDraft
            : undefined,
      })
        .then(() => {
          if (
            caseData?.status === "Pending" ||
            caseData?.status === "Respond_Back_prosecutor"
          ) {
            if (uploadChargeDraft) {
              addChargeDraftHistory({
                case: caseData?.id,
                file: uploadChargeDraft,
                uploaded_by: loginData?.full_name || "",
              });
            }
          }
          showTooltip(t("successful"), "success");
          fetchAll();
        })
        .catch((error) => {
          showTooltip(
            errorHandlerOnForm(error) || t("error_forwarding_case"),
            "error",
          );
        });
    };

    const handleAssignProsecutor = () => {
      setActionDropdownOpen(false);
      setActiveSidebar("form");
      setIsReassignProsecutor(true);
    };

    // Once the final decision file exists the case is finalized — no further
    // actions are allowed here (the Remand tab stays open separately).
    const hasAnyAction =
      !finalDecisionDocument &&
      (canRespondBack ||
        canForward ||
        canAddVenueCourtDetail ||
        canUpdateCourtDetail ||
        canAssignProsecutor ||
        canReassignProsecutor ||
        canUploadFinalDecision);

    return (
      <div className="absolute top-full right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("available_actions")}
        </div>
        {hasAnyAction ? (
          <div className="py-1 max-h-80 overflow-y-auto divide-y divide-gray-50">
            {canRespondBack && (
              <ActionItem
                icon="↩️"
                label={t("respond_back_to_prosecutor")}
                onClick={() => {
                  setActionDropdownOpen(false);
                  setOpenRespondBack(true);
                }}
              />
            )}
            {canForward && (
              <ActionItem
                icon="➡️"
                label={forwardLabel}
                onClick={handleForward}
              />
            )}
            {canAddVenueCourtDetail && (
              <ActionItem
                icon="🏛️"
                label={t("add_court_detail")}
                onClick={() =>
                  navigate("/court-detail", {
                    state: { caseData: caseData, is_venue_changed: true },
                  })
                }
              />
            )}
            {canUpdateCourtDetail && (
              <ActionItem
                icon="✏️"
                label={t("update_court_detail")}
                onClick={() =>
                  navigate("/update-court-detail", {
                    state: { caseData: caseData, is_venue_changed: true },
                  })
                }
              />
            )}
            {canAssignProsecutor && (
              <ActionItem
                icon="👤"
                label={t("assign_prosecutor")}
                onClick={handleAssignProsecutor}
              />
            )}
            {canReassignProsecutor && (
              <ActionItem
                icon="🔁"
                label="Re-assign Prosecutor"
                onClick={handleAssignProsecutor}
              />
            )}
            {canUploadFinalDecision && (
              <ActionItem
                icon="📄"
                label={t("upload_final_decision")}
                onClick={() => {
                  setActionDropdownOpen(false);
                  setActiveSidebar("form");
                }}
              />
            )}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            {t("no_actions_available")}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">{t("loading_case")}</span>
        </div>
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-wrap gap-2">
              {detailTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActivePage(tab.key)}
                  className={`px-6 py-3 font-medium rounded-lg transition-colors ${
                    activePage === tab.key
                      ? "bg-red-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {t(`institute_of_charge_detail_tab_${tab.key}`)}
                </button>
              ))}
              {caseData?.opening_case_files &&
                caseData?.opening_case_files?.length > 0 &&
                extraTabs
                  // The court tab always shows when a court detail exists; the
                  // hearing/report tabs are hidden while the court has an open
                  // update request (is_case_updated_by_court).
                  .filter(
                    (tab) =>
                      tab.key === "court" ||
                      !caseData?.is_case_updated_by_court,
                  )
                  .map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActivePage(tab.key)}
                      className={`px-6 py-3 font-medium rounded-lg transition-colors ${
                        activePage === tab.key
                          ? "bg-red-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {t(`institute_of_charge_detail_tab_${tab.key}`)}
                    </button>
                  ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {activePage === "defendant" && caseData && caseRelatedParties && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <DefendantList
                    caseData={caseData}
                    caseRelatedParties={caseRelatedParties}
                    fetchData={fetchAll}
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator}
                  />
                </div>
              )}

              {activePage === "defendant" &&
                caseData &&
                caseRelatedParties &&
                caseRelatedParties?.suspects?.filter((s) => s?.closed_by_42)
                  .length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <ClosedSusectsList
                      caseData={caseData}
                      caseRelatedParties={caseRelatedParties}
                      fetchData={fetchAll}
                      isProsecutor={isProsecutor}
                      isRegistrar={isRegistrar}
                      isCoordinator={isCoordinator}
                    />
                  </div>
                )}
              {activePage === "witness" && caseData && caseRelatedParties && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <WitnessList
                    caseData={caseData}
                    caseRelatedParties={caseRelatedParties}
                    fetchData={fetchAll}
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator}
                  />
                </div>
              )}
              {activePage === "exibit" && caseData && caseRelatedParties && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <ExibitList
                    caseData={caseData}
                    caseRelatedParties={caseRelatedParties}
                    fetchData={fetchAll}
                    fetchRelatedParties={() =>
                      fetchCaseCreationRelatedParties(caseId || "")
                    }
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator}
                  />
                </div>
              )}

              {activePage === "chargeDraft" && caseData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <ChargeDraft
                    caseData={caseData}
                    fetchData={fetchAll}
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator}
                    isClerk={isClerk}
                    uploadChargeDraft={uploadChargeDraft}
                    setUploadChargeDraft={setUploadChargeDraft}
                    onRespondBack={() => setOpenRespondBack(true)}
                  />
                </div>
              )}

              {activePage === "count" && caseData && caseRelatedParties && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <CountList
                    caseData={caseData}
                    caseRelatedParties={caseRelatedParties}
                    fetchData={fetchAll}
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator}
                  />
                </div>
              )}
              {activePage === "court" && caseData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <CourtList caseData={caseData} />
                </div>
              )}
              {activePage === "hearing" && caseData && caseRelatedParties && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <CourtReport caseData={caseData} fetchAll={fetchAll} />
                </div>
              )}
              {activePage === "report" && caseData && caseRelatedParties && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <Report
                    setSelectedDailyReport={setSelectedDailyReport}
                    selectedDailyReport={selectedDailyReport}
                    caseData={caseData}
                    fetchData={fetchAll}
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator}
                  />
                </div>
              )}
              {activePage === "courtApplication" && caseData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <CourtApplicationTab
                    caseData={caseData}
                    isProsecutor={isProsecutor}
                    isCoordinator={isCoordinator}
                    isRegistrar={isRegistrar}
                    disabled={!!finalDecisionDocument}
                  />
                </div>
              )}
              {activePage === "remand" && caseData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <RemandTab
                    caseData={caseData}
                    isProsecutor={isProsecutor}
                    isCoordinator={isCoordinator}
                    isRegistrar={isRegistrar}
                  />
                </div>
              )}
              {activePage === "execution" && caseData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <ExecutionTab
                    caseData={caseData}
                    isProsecutor={isProsecutor}
                  />
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Sidebar Selector & Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                        {t("view_section")}
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white text-sm font-medium"
                        value={activeSidebar}
                      onChange={(e) => setActiveSidebar(e.target.value)}
                    >
                      {caseData?.is_article_of_cahrge_changed && (
                        <option value="artileOfChange">
                          {t("article_of_charge_change")}
                        </option>
                      )}
                      {((isProsecutor &&
                        (caseData?.status === "Pending" ||
                          caseData?.status === "Respond_Back_prosecutor" ||
                          caseData?.opening_case_files?.length)) ||
                        (isRegistrar &&
                          caseData?.status === "forwarded_to_registrar") ||
                        (caseData?.opening_case_files &&
                          caseData?.opening_case_files[0]?.status ===
                            "forwarded_to_coordinator" &&
                          isCoordinator &&
                          caseData?.case_investigation_links)) &&
                        !caseData?.is_article_of_cahrge_changed &&
                        caseData?.status !== "case_terminated_by_court" && (
                          <option value="form">{t("form")}</option>
                        )}
                      {/* {caseData?.status !== "Pending" && (
                        <option value="chargeDraft">Charge Draft</option>
                      )} */}
                      {finalDecisionDocument && (
                        <option value="finalDecision">
                          {t("final_decision_label")}
                        </option>
                      )}
                      {caseData && (isCoordinator || isProsecutor) && (
                        <option value="respondBack">{t("respond_back")}</option>
                      )}
                      {selectedDailyReport && (
                        <option value="dailyReportFeedback">
                          {t("daily_report_feedback")}
                        </option>
                      )}
                      {caseData && canAddFeedback && (
                        <option value="feedbackSection">{t("feedback")}</option>
                      )}
                      {caseData?.upload_panel_decision_info?.file_name && (
                        <option value="panel">{t("panel_decision")}</option>
                      )}
                      {caseData && isCoordinator && (
                        <option value="activityLog">{t("activity_log")}</option>
                      )}
                      </select>
                    </div>

                    {!caseData?.is_article_of_cahrge_changed &&
                      caseData?.status !== "case_terminated_by_court" && (
                        <div className="relative" ref={actionDropdownRef}>
                          <button
                            type="button"
                            className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                            onClick={() =>
                              setActionDropdownOpen(!actionDropdownOpen)
                            }
                          >
                            {t("take_action")}
                            <svg
                              className={`w-4 h-4 transition-transform ${
                                actionDropdownOpen ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          {actionDropdownOpen && renderActionDropdown()}
                        </div>
                      )}
                  </div>
                </div>

                <div className="p-6">
                  {activeSidebar === "dailyReportFeedback" && (
                    <DailyReportFeedbackSection
                      selectedDailyReport={selectedDailyReport}
                      selectedCase={caseData}
                      fetchData={fetchAll}
                    />
                  )}
                  {activeSidebar === "artileOfChange" && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        {t("reason_for_article_change")}
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {caseData?.reason_for_articel_of_charge ??
                          t("no_reason_provided")}
                      </p>
                    </div>
                  )}
                  {activeSidebar === "form" &&
                    caseData?.opening_case_files &&
                    caseData?.opening_case_files[0]?.status ===
                      "forwarded_to_coordinator" &&
                    isCoordinator &&
                    caseData?.case_investigation_links &&
                    !caseData?.opening_case_files[0].assign_prosecutor &&
                    isReassignProsecutor && (
                      <AssignedProsecutorToCourt
                        file={caseData?.opening_case_files[0]}
                        caseData={caseData}
                        assignedProsecutor={
                          caseData?.case_investigation_links[0]
                            ?.investigation_file_info?.assign_prosecutor
                        }
                        setRefetch={() => {
                          fetchAll();
                          setActiveSidebar("feedbackSection");
                          setIsReassignProsecutor(false);
                        }}
                      />
                    )}
                  {isCoordinator &&
                    activeSidebar === "form" &&
                    caseData?.opening_case_files &&
                    caseData?.case_investigation_links &&
                    caseData?.opening_case_files[0].assign_prosecutor &&
                    isReassignProsecutor && (
                      <ReAssignedProsecutorToCourt
                        file={caseData?.opening_case_files[0]}
                        caseData={caseData}
                        assignedProsecutor={
                          caseData?.case_investigation_links[0]
                            ?.investigation_file_info?.assign_prosecutor
                        }
                        setRefetch={() => {
                          fetchAll();
                          setActiveSidebar("feedbackSection");
                          setIsReassignProsecutor(false);
                        }}
                      />
                    )}
                  {activeSidebar === "form" &&
                    isProsecutor &&
                    !caseData?.is_case_updated_by_court &&
                    caseData?.opening_case_files &&
                    caseData?.opening_case_files?.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                          <div className="w-2 h-8 bg-red-600 rounded-full" />
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {t("upload_final_decision")}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {t("upload_final_decision_desc", {
                                defaultValue:
                                  "Attach the court's final decision document for this case.",
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="p-6 space-y-4">
                          {/* Current final decision (if already uploaded) */}
                          {finalDecisionDocument && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                              <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                                {t("current_final_decision", {
                                  defaultValue: "Current Final Decision",
                                })}
                              </p>
                              <DocumentViewerModal
                                documents={[finalDecisionDocument] as any}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CASE_CREATION}`}
                                layout="list"
                                hideEmptyFile={true}
                              />
                            </div>
                          )}

                          <FileUpload
                            label={t("final_decision_document")}
                            fileTypes="*"
                            info={t("upload_files")}
                            required
                            onChange={(file) => setFinalDecision(file)}
                          />

                          <div className="flex justify-end gap-3 pt-2">
                            <button
                              type="button"
                              onClick={() => {
                                setFinalDecision(null);
                                setActiveSidebar("feedbackSection");
                              }}
                              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
                            >
                              {t("cancel", { defaultValue: "Cancel" })}
                            </button>
                            <button
                              type="button"
                              disabled={!finalDecision || finalDecisionSubmitting}
                              onClick={handleUploadFinalDecision}
                              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              {finalDecisionSubmitting
                                ? t("submitting", {
                                    defaultValue: "Submitting...",
                                  })
                                : t("submit_final_decision", {
                                    defaultValue: "Submit Final Decision",
                                  })}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  {/* {activeSidebar === "chargeDraft" && caseData && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-red-600">{feedbackIcon}</span>
                        Charge Draft History
                      </h3>
                      <div className="mt-4">
                        <DocumentViewerModal
                          documents={
                            (caseData?.documents?.filter(
                              (a) => a.document_type === "upload_charge_draft",
                            ) || []) as any
                          }
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CASE_CREATION}`}
                          layout="list"
                          label="Charge Draft History"
                          hideEmptyFile={true}
                        />
                      </div>
                    </div>
                  )} */}
                  {activeSidebar === "finalDecision" && caseData && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-red-600">{feedbackIcon}</span>
                        {t("final_decision_document")}
                      </h3>
                      <div className="mt-4">
                        <DocumentViewerModal
                          documents={
                            (caseData?.documents?.filter(
                              (a) => a.document_type === "final_court_decision",
                            ) || []) as any
                          }
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CASE_CREATION}`}
                          layout="list"
                          label={t("final_decision_document")}
                          hideEmptyFile={true}
                        />
                      </div>
                    </div>
                  )}
                  {activeSidebar === "feedbackSection" && caseData && (
                    <FeedbackSection
                      selectedCase={caseData}
                      setRefetch={() => fetchAll()}
                    />
                  )}
                  {activeSidebar === "respondBack" && caseData && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-red-600">{feedbackIcon}</span>
                        {t("respond_back_section")}
                      </h3>
                      {caseData?.respond_comments?.map((comment, index) => (
                        <div key={index} className="mb-6 last:mb-0">
                          <div className="mb-3">
                            <span className="text-red-600 font-semibold text-base">
                              {t("responded_back")}
                            </span>
                            <p className="text-gray-600 text-sm mt-2">
                              {comment.comment}
                            </p>
                          </div>
                          <DocumentViewerModal
                            documents={(comment?.documents || []) as any}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.FEEBACK_FILES}`}
                            layout="list"
                            hideEmptyFile={true}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {activeSidebar === "panel" && caseData && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-red-600">{feedbackIcon}</span>
                        {t("panel_decision_section")}
                      </h3>
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {caseData?.upload_panel_decision_info
                                ?.file_name || t("document")}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              window.open(
                                caseData?.upload_panel_decision_info?.file_url,
                                "_blank",
                              );
                            }}
                          >
                            {t("view")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeSidebar === "activityLog" &&
                    caseData &&
                    caseRelatedParties && <ChangeLog caseData={caseData} />}
                </div>
              </div>
            </div>
          </div>

          {caseData?.id && (
            <DirectorNotesButton
              model={FEEDBACK_MODEL.case}
              objectId={caseData.id}
              role={isProsecutor ? "prosecutor" : undefined}
            />
          )}
        </>
      )}

      {openRespondBack && caseData?.id !== undefined && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md border border-gray-200">
            <RespondBackComment
              caseId={caseData.id}
              onClose={(refetch) => {
                setOpenRespondBack(false);
                if (refetch) fetchAll();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InstituteOfChargeDetail;
